import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldAlert, BookOpen, Gavel, AlertTriangle } from "lucide-react";

const DOCS = [
  { slug: "trust-safety-policy", title: "Trust & Safety Enforcement Policy", desc: "How EthicCred enforces its policies — detection, review, actions, and appeals.", icon: ShieldAlert },
  { slug: "community-guidelines", title: "Community Guidelines", desc: "Behavioural standards every EthicCred member follows.", icon: BookOpen },
  { slug: "dispute-resolution-policy", title: "Dispute Resolution Policy", desc: "How to challenge a record and what we do with it.", icon: Gavel },
];

const FUTURE = [
  { title: "Professional Incident Management Policy", desc: "Governs allegations involving fraud, harassment, violence, regulatory violations, or financial misconduct. Coming soon." },
];

export default function TrustSafety() {
  return (
    <div className="min-h-screen bg-ec-bg" data-testid="trust-safety-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14">
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow">Trust & Safety</div>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-3">
            The integrity layer of the global workforce.
          </h1>
          <p className="text-ec-ink-soft mt-5 leading-relaxed">
            EthicCred is only useful if every record it holds can be trusted. Our Trust & Safety
            team works around the clock to detect fraud, enforce community standards, and resolve
            disputes fairly.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-16">
          {DOCS.map((d) => (
            <Link to={`/legal/${d.slug}`} key={d.slug} data-testid={`ts-doc-${d.slug}`}>
              <Card className="rounded-sm border-ec-line hover:-translate-y-0.5 transition-transform h-full">
                <CardContent className="p-6">
                  <d.icon className="w-6 h-6 text-ec-emerald" />
                  <div className="font-display font-bold text-lg mt-3">{d.title}</div>
                  <div className="text-sm text-ec-ink-soft mt-2">{d.desc}</div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="mt-14 border-t border-ec-line pt-10">
          <h2 className="font-display text-2xl font-black tracking-tight">In progress</h2>
          <div className="mt-4 space-y-3">
            {FUTURE.map((f) => (
              <div key={f.title} className="border border-dashed border-ec-line bg-ec-soft p-5 rounded-sm flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-ec-amber mt-0.5" />
                <div>
                  <div className="font-display font-bold">{f.title}</div>
                  <div className="text-sm text-ec-ink-soft mt-1">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-ec-ink text-white p-8 sm:p-10">
          <div className="eyebrow text-zinc-400">Report something</div>
          <h3 className="font-display text-2xl font-black mt-2">See a problem? Tell us.</h3>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            Suspicious certificates, impersonation, or abuse of the Platform can be reported
            through the dispute system on your dashboard, or by writing to <span className="font-mono-plex text-ec-emerald">trust@ethiccred.com</span>.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
